import './SettingsView.css'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, safety } from '../store'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { Button } from '../components/Button'
import { SegmentedControl } from '../components/SegmentedControl'
import {
  getColorSchemePreference,
  setColorSchemePreference,
  type ColorSchemePreference,
} from '../services/color-scheme'

const appearanceOptions: { value: ColorSchemePreference; label: string }[] = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
]

export function SettingsView() {
  useRequireAuthentication()

  const navigate = useNavigate()
  const [, setBreadcrumbs] = useBreadcrumbs()
  const currentSession = useAtomValue(session.sessionAtom)
  const logout = useSetAtom(session.logoutAtom)
  const deleteAccount = useSetAtom(safety.deleteAccountAtom)
  const isLoading = useAtomValue(safety.safetyIsLoadingAtom)
  const error = useAtomValue(safety.safetyErrorAtom)

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deletePhrase, setDeletePhrase] = useState('')
  const [appearance, setAppearance] = useState<ColorSchemePreference>(() =>
    getColorSchemePreference(),
  )

  useEffect(() => {
    setBreadcrumbs([{ label: 'Settings' }])
  }, [setBreadcrumbs])

  const handleAppearance = (value: ColorSchemePreference) => {
    setAppearance(value)
    setColorSchemePreference(value)
  }

  const handleDelete = async () => {
    if (deletePhrase !== 'DELETE') return
    const result = await deleteAccount()
    if (result.success) {
      await logout()
      navigate('/login', { replace: true })
    }
  }

  const tier = currentSession?.user.subscriptionTier ?? 'free'
  const status = currentSession?.user.subscriptionStatus ?? 'active'
  const isPaid = tier === 'paid' && (status === 'active' || status === 'trialing')

  return (
    <div className="settings-view">
      <h1 className="settings-view__title display-title">Settings</h1>

      <section className="settings-view__section">
        <h2 className="settings-view__heading">Account</h2>
        <p className="settings-view__text">
          Signed in as <strong>{currentSession?.user.email}</strong>
        </p>
        <p className="settings-view__meta">
          Subscription: {tier} ({status})
        </p>
      </section>

      <section className="settings-view__section">
        <h2 className="settings-view__heading">Plan &amp; billing</h2>
        <p className="settings-view__text">
          {isPaid ? (
            <>
              You&apos;re on the <strong>paid</strong> membership ({status}). Free members get fewer
              introductions each cycle; paid raises capacity while keeping the same quality bar.
            </>
          ) : (
            <>
              You&apos;re on the <strong>free</strong> plan ({status}). Free members get fewer
              introductions each cycle. Paid membership raises capacity while keeping the same
              quality bar.
            </>
          )}{' '}
          Plan changes and billing management will open here soon.
        </p>
        <div className="settings-view__delete-actions">
          <Button
            kind="primary"
            text={isPaid ? 'Manage plan — coming soon' : 'Upgrade — coming soon'}
            disabled
          />
        </div>
      </section>

      <section className="settings-view__section">
        <h2 className="settings-view__heading">Appearance</h2>
        <p className="settings-view__text">
          Dark is the Revel Club default. Choose light or follow your system preference anytime.
        </p>
        <SegmentedControl
          ariaLabel="Color appearance"
          options={appearanceOptions}
          value={appearance}
          onChange={handleAppearance}
        />
      </section>

      <section className="settings-view__section settings-view__section--danger">
        <h2 className="settings-view__heading">Delete account</h2>
        <p className="settings-view__text">
          This deactivates your account, hides your profile, dismisses matches, and signs you out.
          You will not be able to sign in with this email again unless you register a new account.
        </p>

        {!confirmDelete ? (
          <Button kind="ghost" text="Delete my account" onClick={() => setConfirmDelete(true)} />
        ) : (
          <div className="settings-view__delete-confirm">
            <label className="settings-view__label" htmlFor="delete-confirm">
              Type DELETE to confirm
            </label>
            <input
              id="delete-confirm"
              className="settings-view__input"
              value={deletePhrase}
              onChange={(event) => setDeletePhrase(event.target.value)}
              disabled={isLoading}
              autoComplete="off"
            />
            <div className="settings-view__delete-actions">
              <Button
                kind="ghost"
                text="Cancel"
                onClick={() => {
                  setConfirmDelete(false)
                  setDeletePhrase('')
                }}
                disabled={isLoading}
              />
              <Button
                kind="primary"
                text={isLoading ? 'Deleting…' : 'Permanently delete'}
                onClick={handleDelete}
                disabled={isLoading || deletePhrase !== 'DELETE'}
              />
            </div>
          </div>
        )}

        {error && <p className="settings-view__error">{error}</p>}
      </section>
    </div>
  )
}
